import api from '@/utils/axios';
import { getMe } from '@/domains/auth/services/auth.service';
import { getSession } from '@/domains/auth/services/auth.service';
import { listUserDocuments } from '@/domains/api/documents.api';
import { BUSINESS_API_AVAILABLE, DRIVER_API_AVAILABLE, ORDER_API_AVAILABLE } from '@/domains/api';
import {
  listDriverOrderRequests,
  updateOrderRequestStatus,
} from '@/domains/api/delivery.api';
import {
  fetchDriverDashboard as apiFetchDriverDashboard,
  fetchDriverWallet as apiFetchDriverWallet,
  withdrawDriverWallet as apiWithdrawDriverWallet,
  fetchDriverRoute as apiFetchDriverRoute,
  fetchDriverZones as apiFetchDriverZones,
  fetchDriverInsurance as apiFetchDriverInsurance,
  fetchDriverTrips as apiFetchDriverTrips,
  createDriverTrip as apiCreateDriverTrip,
  updateDriverTrip as apiUpdateDriverTrip,
  deleteDriverTrip as apiDeleteDriverTrip,
  listMyDriverOrderRequests,
  listAvailableDriverOrderRequests,
  acceptDriverOrderRequest,
  dismissDriverOrderRequest,
  type DriverDashboardData,
  type DriverWalletData,
  type DriverRouteData,
  type DriverZonesData,
  type DriverInsuranceData,
  type DriverTrip,
  type DriverWalletTransaction,
} from '@/domains/api/driver.api';
import { sendDriverLocation } from '@/domains/api/tracking.api';
import type { ApiOrderRequest } from '@/domains/api/types';

export interface DriverProfile {
  id: number;
  firstnames: string;
  lastname: string;
  email: string | null;
  phone: string;
  status: string;
  zone?: { id: number; name: string };
  vehicleCapacity?: {
    id: number;
    vehicle_type_id: number;
    max_weight: number;
    max_volume: number;
    vehicleType: { id: number; name: string };
  };
  specializations?: Array<{ id: number; name: string }>;
  availabilitySlots?: Array<{ id: number; name: string; start_time?: string | null; end_time?: string | null }>;
  coverageZones?: Array<{ id: number; name: string }>;
}

export interface Delivery {
  id: number;
  commande_id: number;
  livreur_id?: number | null;
  statut: string;
  commande?: {
    id: number;
    code_suivi: string;
    statut: string;
    adresse_ramassage: string;
    adresse_livraison: string;
    distance_km?: number;
    prix_estime?: number;
    client?: { id: number; firstnames: string; lastname: string; phone: string };
  };
}

export interface DriverDocument {
  id: number;
  user_id: number;
  category_id: number;
  file_url: string;
  status: string;
  started_at: string;
  ended_at: string;
  category?: { id: number; name: string };
}

export interface DriverStats {
  total_deliveries: number;
  completed_deliveries: number;
  cancelled_deliveries: number;
  total_earnings: number;
  average_rating: number;
  today_earnings: number;
  week_earnings: number;
  month_earnings: number;
}

export interface DriverMission {
  id: number;
  code: string;
  status: string;
  isAvailable?: boolean;
  deliveryType: string;
  packageWeight: number;
  packageVolume?: number;
  packageDescription: string;
  paymentMethod: string;
  price: number;
  createdAt: string;
  pickup: {
    zone: string;
    address: string;
    landmark?: string | null;
  };
  dropoff: {
    zone: string;
    address: string;
    landmark?: string | null;
  };
  client: {
    id: number;
    firstnames: string;
    lastname: string;
    phone: string;
  } | null;
}

function toNumber(value: number | string | null | undefined): number {
  if (value == null) return 0;
  return typeof value === 'number' ? value : parseFloat(value) || 0;
}

function mapOrderRequestToMission(raw: ApiOrderRequest, isAvailable = false): DriverMission {
  const client = raw.created_by;
  return {
    id: raw.id,
    code: `CMD-${raw.id}`,
    status: raw.status,
    isAvailable,
    deliveryType: raw.delivery_type?.name ?? '—',
    packageWeight: toNumber(raw.estimated_weight),
    packageVolume: raw.estimated_volume != null ? toNumber(raw.estimated_volume) : undefined,
    packageDescription: raw.package_description,
    paymentMethod: raw.payment_method?.name ?? '—',
    price: toNumber(raw.total_delivery_fee),
    createdAt: raw.created_at ?? new Date().toISOString(),
    pickup: {
      zone: raw.departure_secteur?.name ?? raw.departure_zone?.name ?? '—',
      address: raw.departure_address,
      landmark: raw.departure_landmark,
    },
    dropoff: {
      zone: raw.arrival_secteur?.name ?? raw.arrival_zone?.name ?? '—',
      address: raw.arrival_address,
      landmark: raw.arrival_landmark,
    },
    client: client
      ? {
          id: client.id,
          firstnames: client.firstnames,
          lastname: client.lastname,
          phone: client.phone,
        }
      : null,
  };
}

async function getDriverId(): Promise<number> {
  const session = getSession();
  if (session?.user?.id) return session.user.id;
  const me = await getMe();
  return me.id as number;
}

function normalizeProfile(raw: Record<string, unknown>): DriverProfile {
  const vc = raw.vehicle_capacity as Record<string, unknown> | undefined;
  const vehicleType = (vc?.vehicle_type ?? raw.vehicle_type) as { id: number; name: string } | undefined;

  return {
    id: raw.id as number,
    firstnames: raw.firstnames as string,
    lastname: raw.lastname as string,
    email: (raw.email as string | null) ?? null,
    phone: raw.phone as string,
    status: raw.status as string,
    zone: raw.zone as DriverProfile['zone'],
    vehicleCapacity: vc
      ? {
          id: vc.id as number,
          vehicle_type_id: vc.vehicle_type_id as number,
          max_weight: vc.max_weight as number,
          max_volume: vc.max_volume as number,
          vehicleType: vehicleType ?? { id: 0, name: '—' },
        }
      : undefined,
    specializations: (raw.specializations as DriverProfile['specializations']) ?? [],
    availabilitySlots: (raw.availability_slots as DriverProfile['availabilitySlots']) ?? [],
    coverageZones: (raw.coverage_zones as DriverProfile['coverageZones']) ?? [],
  };
}

const DEMO_DELIVERIES: Delivery[] = [
  {
    id: 1,
    commande_id: 101,
    statut: 'en_attente',
    commande: {
      id: 101,
      code_suivi: 'ONA-2026-0142',
      statut: 'en_attente',
      adresse_ramassage: 'Kaloum, Conakry',
      adresse_livraison: 'Ratoma, Conakry',
      distance_km: 4.2,
      prix_estime: 35000,
      client: { id: 1, firstnames: 'Aminata', lastname: 'Diallo', phone: '+224620000001' },
    },
  },
];

export async function fetchDriverProfile(): Promise<DriverProfile> {
  const user = await getMe();
  return normalizeProfile(user as unknown as Record<string, unknown>);
}

/** Endpoint non exposé dans routes/api.php */
export async function setAvailability(_isAvailable: boolean): Promise<void> {
  if (!BUSINESS_API_AVAILABLE) return;
  await api.patch('/users/me/availability', { is_available: _isAvailable });
}

export async function fetchDriverDeliveries(): Promise<Delivery[]> {
  if (!BUSINESS_API_AVAILABLE) return DEMO_DELIVERIES;
  const res = await api.get('/deliveries/driver');
  const list = (res.data.data ?? res.data ?? []) as Delivery[];
  return list.length > 0 ? list : DEMO_DELIVERIES;
}

export async function fetchDriverMissions(status?: string): Promise<DriverMission[]> {
  if (!ORDER_API_AVAILABLE) return [];
  if (status === 'available') {
    const orders = await listAvailableDriverOrderRequests();
    return (orders as ApiOrderRequest[]).map((o) => mapOrderRequestToMission(o, true));
  }
  const orders = DRIVER_API_AVAILABLE
    ? await listMyDriverOrderRequests(status)
    : await listDriverOrderRequests(await getDriverId(), status);
  return (orders as ApiOrderRequest[]).map((o) => mapOrderRequestToMission(o, false));
}

export async function fetchAvailableDriverMissions(): Promise<DriverMission[]> {
  return fetchDriverMissions('available');
}

export async function acceptDriverMission(missionId: number, isAvailable = false): Promise<DriverMission> {
  if (isAvailable) {
    const updated = await acceptDriverOrderRequest(missionId);
    return mapOrderRequestToMission(updated as ApiOrderRequest, false);
  }
  const updated = await updateOrderRequestStatus(missionId, 'in_progress');
  return mapOrderRequestToMission(updated);
}

export async function rejectDriverMission(missionId: number, isAvailable = false): Promise<DriverMission> {
  if (isAvailable) {
    await dismissDriverOrderRequest(missionId);
    return mapOrderRequestToMission({ id: missionId, status: 'dismissed' } as ApiOrderRequest, true);
  }
  const updated = await updateOrderRequestStatus(missionId, 'rejected');
  return mapOrderRequestToMission(updated);
}

export async function completeDriverMission(missionId: number): Promise<DriverMission> {
  const updated = await updateOrderRequestStatus(missionId, 'delivered');
  return mapOrderRequestToMission(updated);
}

export async function acceptDelivery(deliveryId: number): Promise<Delivery> {
  if (!BUSINESS_API_AVAILABLE) throw new Error('Endpoint livraisons non disponible');
  const res = await api.patch(`/deliveries/${deliveryId}/accept`);
  return res.data.data ?? res.data;
}

export async function startDelivery(deliveryId: number): Promise<Delivery> {
  if (!BUSINESS_API_AVAILABLE) throw new Error('Endpoint livraisons non disponible');
  const res = await api.patch(`/deliveries/${deliveryId}/start`);
  return res.data.data ?? res.data;
}

export async function completeDelivery(deliveryId: number, proofUrl?: string): Promise<Delivery> {
  if (!BUSINESS_API_AVAILABLE) throw new Error('Endpoint livraisons non disponible');
  const res = await api.patch(`/deliveries/${deliveryId}/complete`, { proof_url: proofUrl });
  return res.data.data ?? res.data;
}

export async function cancelDelivery(deliveryId: number, reason: string): Promise<void> {
  if (!BUSINESS_API_AVAILABLE) throw new Error('Endpoint livraisons non disponible');
  await api.patch(`/deliveries/${deliveryId}/cancel`, { reason });
}

export async function fetchDriverDocuments(): Promise<DriverDocument[]> {
  const session = getSession();
  const userId = session?.user?.id;
  if (!userId) return [];
  return listUserDocuments(userId);
}

export async function fetchDriverStats(): Promise<DriverStats> {
  if (!DRIVER_API_AVAILABLE) {
    return {
      total_deliveries: 0,
      completed_deliveries: 0,
      cancelled_deliveries: 0,
      total_earnings: 0,
      average_rating: 0,
      today_earnings: 0,
      week_earnings: 0,
      month_earnings: 0,
    };
  }
  const dashboard = await apiFetchDriverDashboard();
  const s = dashboard.stats;
  return {
    total_deliveries: s.total_deliveries,
    completed_deliveries: s.completed_deliveries,
    cancelled_deliveries: s.cancelled_deliveries + s.rejected_deliveries,
    total_earnings: s.total_earnings,
    average_rating: s.average_rating,
    today_earnings: s.today_earnings,
    week_earnings: s.week_earnings,
    month_earnings: s.month_earnings,
  };
}

export async function fetchDriverDashboard(): Promise<DriverDashboardData> {
  return apiFetchDriverDashboard();
}

export async function fetchDriverWallet(): Promise<DriverWalletData> {
  return apiFetchDriverWallet();
}

export async function withdrawDriverWallet(payload: WalletOperationPayload): Promise<DriverWalletData> {
  return apiWithdrawDriverWallet(payload);
}

export async function fetchDriverRoute(): Promise<DriverRouteData> {
  return apiFetchDriverRoute();
}

export async function fetchDriverZones(): Promise<DriverZonesData> {
  return apiFetchDriverZones();
}

export async function fetchDriverInsurance(): Promise<DriverInsuranceData> {
  return apiFetchDriverInsurance();
}

export async function fetchDriverTrips(): Promise<DriverTrip[]> {
  return apiFetchDriverTrips();
}

export async function createDriverTrip(payload: {
  departure_city: string;
  arrival_city: string;
  departure_date: string;
  available_space: number;
  price_per_kg: number;
}): Promise<DriverTrip> {
  return apiCreateDriverTrip(payload);
}

export async function updateDriverTrip(
  id: number,
  payload: Partial<{
    departure_city: string;
    arrival_city: string;
    departure_date: string;
    available_space: number;
    price_per_kg: number;
    status: string;
  }>,
): Promise<DriverTrip> {
  return apiUpdateDriverTrip(id, payload);
}

export async function deleteDriverTrip(id: number): Promise<void> {
  return apiDeleteDriverTrip(id);
}

export type {
  DriverDashboardData,
  DriverWalletData,
  DriverRouteData,
  DriverZonesData,
  DriverInsuranceData,
  DriverTrip,
  WalletOperationPayload,
  DriverWalletTransaction,
};

export async function updateLocation(
  latitude: number,
  longitude: number,
  orderRequestId?: number,
): Promise<void> {
  await sendDriverLocation({
    latitude,
    longitude,
    order_request_id: orderRequestId,
  });
}
